import { call, put, select, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import {
  getCatalogCollection as fetchCatalogCollectionAPI,
  createCatalogCollection as createCatalogCollectionAPI,
  previewCatalogCollection as previewCatalogCollectionAPI,
  updateCatalogCollection as updateCatalogCollectionAPI,
  getFieldMappings,
} from 'api/CatalogAPI';
import { GenericDataType } from 'modules/domains';
import { getMappings } from 'api/MappingsAPI';
import { selectFeatureFlags } from 'core/userContext';
import { queryBuilderSelector } from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import * as q from 'modules/queryBuilder/legacyQueryBuilder/actions';
import * as h from 'modules/queryBuilder/legacyQueryBuilder/helpers';
import { fetchCatalogFieldTypes } from 'modules/catalog/actions';
import { setUsesSmartRecosHandlebar } from 'modules/catalog/collections/actions';
import { CATALOG_COLLECTION_NAME } from 'modules/catalog/catalogConstants';
import { isFunction } from 'utils/helpers';
import { addPositiveToast, addNegativeToastWithErrorId } from 'modules/toastContainer';
import { collectionBuilderSelector } from './selectors';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { getCatalogFieldMapping } from './utils';
import { errorIds } from '../errorIds';

/**
 * changeCatalog
 * Fetches a catalog fields after changing catalog.
 * @param { Object } params: Params for the saga containing the catalog.
 * @returns { void }
 */
export function* changeCatalog(params) {
  try {
    const { catalog, fetchFieldTypes } = params;
    if (catalog && fetchFieldTypes) {
      yield put(fetchCatalogFieldTypes(catalog));
    }
  } catch (error) {
    yield put(actions.updateCollectionError(error));
  }
}

/**
 * createParamsForSave
 * Creates formatted param object for saving a catalog collection.
 * @param { Object } collectionBuilderState: Props from the catalog collection builder state.
 * @param { Object } queryBuilderState: Props from the base query builder state.
 * @param { Object } selectedCollection: If passed in, will get props from here instead of queryBuilder state.
 * @return { Object } The formatted params to save.
 */
export const createParamsForSave = (
  collectionBuilderState,
  queryBuilderState,
  selectedCollection,
) => {
  const { catalog, description, name } = collectionBuilderState;
  const { combinator, orderBy, searchQueries, sizeLimit, usesSmartRecos } =
    selectedCollection || queryBuilderState;
  return {
    catalogName: catalog,
    description: description || '',
    name,
    orderBy: h.formatOrderByForSave(orderBy),
    sizeLimit: parseFloat(sizeLimit, 10),
    searchQuery: { combinator, searchQueries },
    usesSmartRecos,
  };
};

/**
 * parseFetchError
 * Helper function for pasring error on fetching.
 * @param { Object } error: The error response object.
 * @param { String } id: The id of the catalog collection that was being fetched.
 * @returns { Object } The parsed error object.
 */
export const parseFetchError = (error, id) => {
  if (error && error.response) {
    const { data, status } = error.response;
    const message = data?.startsWith?.('No')
      ? data
      : `${CATALOG_COLLECTION_NAME} Id (${id}) must be a number!`;
    return { ...error.response, message, collectionNotFound: status === 400 };
  }
  return {};
};

/**
 * runCallback
 * Helper function to run a callback function is it exists.
 * @param { Function } callback: The callback function to run.
 * @returns { Boolean } Function called.
 */
export const runCallback = (callback) => {
  if (isFunction(callback)) {
    callback();
  }
  return true;
};

/**
 * fetchCatalogCollection
 * Fetches a catalog collection.
 * @param { Object } params: Params for the saga containing the catalog collection id.
 * @returns { void }
 */
export function* fetchCatalogCollection({ id }) {
  try {
    yield put(q.toggleViewMode());
    const response = yield call(fetchCatalogCollectionAPI, id);
    const { data } = response;
    const { usesSmartRecos } = data;
    yield put(actions.fetchCollectionResponse(data));
    yield put(setUsesSmartRecosHandlebar(usesSmartRecos));
    yield put(q.fetchQueryResponse(h.parseSearchQueryResponseData(data)));
  } catch (error) {
    const errorResponse = parseFetchError(error, id);
    yield put(actions.fetchCollectionError(errorResponse));
    yield put(q.fetchQueryResponse(errorResponse));
    if (!errorResponse.collectionNotFound) {
      yield put(
        addNegativeToastWithErrorId(
          `Error fetching ${CATALOG_COLLECTION_NAME}.`,
          errorIds.catalogCollection.fetch,
          error,
        ),
      );
    }
  }
}

/**
 * createCatalogCollection
 * Creates a catalog collection.
 * @returns { void }
 */
export function* createCatalogCollection() {
  try {
    const catalogQueryBuilderState = yield select(collectionBuilderSelector);
    const baseQueryBuilderState = yield select(queryBuilderSelector);
    const params = createParamsForSave(catalogQueryBuilderState, baseQueryBuilderState);
    const response = yield call(createCatalogCollectionAPI, params);
    const { id } = response.data;
    const { usesSmartRecos } = params;
    yield put(actions.createCollectionResponse(id));
    yield put(setUsesSmartRecosHandlebar(usesSmartRecos));
    yield put(addPositiveToast(`Successfully created ${params.name}`));
  } catch (error) {
    yield put(actions.createCollectionError(error));
    yield put(
      addNegativeToastWithErrorId(
        `Error creating ${CATALOG_COLLECTION_NAME}.`,
        errorIds.catalogCollection.create,
        error,
      ),
    );
  }
}

/**
 * updateCatalogCollection
 * Updates a catalog collection.
 * @param { Object } actionProps: Props from the update action.
 * @returns { void }
 */
export function* updateCatalogCollection({ selectedCollection, callback } = {}) {
  try {
    const { id, ...catalogQueryBuilderState } = yield select(collectionBuilderSelector);
    const baseQueryBuilderState = yield select(queryBuilderSelector);
    const params = createParamsForSave(
      catalogQueryBuilderState,
      baseQueryBuilderState,
      selectedCollection,
    );
    const { usesSmartRecos } = params;
    yield call(updateCatalogCollectionAPI, id, params);
    yield put(actions.updateCollectionResponse(id));
    yield put(setUsesSmartRecosHandlebar(usesSmartRecos));
    yield put(addPositiveToast(`Successfully saved ${params.name}`));
    runCallback(callback);
  } catch (error) {
    yield put(actions.updateCollectionError(error));
    yield put(
      addNegativeToastWithErrorId(
        `Error updating ${CATALOG_COLLECTION_NAME}.`,
        errorIds.catalogCollection.update,
        error,
      ),
    );
  }
}

export const createOrUpdate = (isUpdate /*: boolean */) =>
  isUpdate ? updateCatalogCollection : createCatalogCollection;

/**
 * previewCatalogCollection
 * Preview a catalog collection.
 * @param { Object } params: Params for the saga containing the user email to preview collection with.
 * @returns { void }
 */
export function* previewCatalogCollection(params) {
  try {
    const { canSave, isExistingCollection, userIdentifier, isEmail } = params;

    if (canSave) {
      const saveSaga = createOrUpdate(isExistingCollection);
      yield call(saveSaga);
    }
    const { name } = yield select(collectionBuilderSelector);
    const response = isEmail
      ? yield call(previewCatalogCollectionAPI, name, userIdentifier)
      : yield call(previewCatalogCollectionAPI, name, null, userIdentifier);
    const { data } = response;
    yield put(actions.previewCollectionResponse(data));
  } catch (error) {
    const defaultResponseError = { data: { message: '' } };
    const {
      response: {
        data: { message },
      } = defaultResponseError,
    } = error;
    yield put(actions.previewCollectionError(error));
    yield put(
      addNegativeToastWithErrorId(
        `Error previewing ${CATALOG_COLLECTION_NAME}.
        ${message}`,
        errorIds.catalogCollection.preview,
        error,
      ),
    );
  }
}

export function* fetchFieldMappings() {
  try {
    const { DeprecateGetFieldMappingsEndpointInUI: isNewMappingEndpointEnabled } =
      yield select(selectFeatureFlags);
    if (isNewMappingEndpointEnabled) {
      const data = yield call(getMappings, GenericDataType.User);
      const mappings = data.map(getCatalogFieldMapping);
      yield put(actions.fetchFieldMappingsResponse(mappings));
    } else {
      const data = yield call(getFieldMappings);
      yield put(actions.fetchFieldMappingsResponse(data));
    }
  } catch (error) {
    yield put(actions.fetchFieldMappingsError(error));
  }
}

addToRootSaga([
  takeLatest(actionTypes.CHANGE_CATALOG, changeCatalog),
  takeLatest(actionTypes.FETCH_COLLECTION, fetchCatalogCollection),
  takeLatest(actionTypes.CREATE_COLLECTION, createCatalogCollection),
  takeLatest(actionTypes.UPDATE_COLLECTION, updateCatalogCollection),
  takeLatest(actionTypes.PREVIEW_COLLECTION, previewCatalogCollection),
  takeLatest(actionTypes.FETCH_FIELD_MAPPING_REQUEST, fetchFieldMappings),
]);
